import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Sidebar.css';

const PROJECT_STAGE_LINKS = [
  'Lead Received',
  'Call NR',
  'Not Interested',
  'Irrelevant Lead',
  'In Progress',
  'Quotation Sent',
  'Site Visit Done',
  'Office Visit Done',
  'Revised Quotation Sent',
  'Client Loss',
  'Client Won',
];

const NAV_SECTIONS = [
  {
    label: 'Main',
    items: [
      { to: '/dashboard', icon: 'D', label: 'Dashboard' },
      { to: '/projects', icon: 'P', label: 'Projects', children: PROJECT_STAGE_LINKS.map((stage) => ({ to: `/projects?stage=${encodeURIComponent(stage)}`, label: stage, stage })) },
      { to: '/tasks', icon: 'T', label: 'Tasks' },
      { to: '/payments', icon: '$', label: 'Financials' },
      { to: '/activity', icon: 'A', label: 'Activity Logs' },
    ],
  },
  {
    label: 'Masters',
    items: [
      { to: '/vendors', icon: 'V', label: 'Vendors' },
      { to: '/item-master', icon: 'I', label: 'Item Master' },
      { to: '/material-master', icon: 'M', label: 'Material Master' },
      { to: '/inventory', icon: 'IN', label: 'Inventory' },
    ],
  },
  {
    label: 'Manage',
    items: [
      { to: '/register', icon: 'U+', label: 'Users', adminOnly: true },
      { to: '/settings', icon: 'S', label: 'Settings' },
    ],
  },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const selectedStage = new URLSearchParams(location.search).get('stage') || '';
  const [projectMenuOpen, setProjectMenuOpen] = useState(() => {
    try {
      return localStorage.getItem('sidebar-project-menu-open') !== 'false';
    } catch {
      return true;
    }
  });

  const toggleProjectMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setProjectMenuOpen((open) => {
      const next = !open;
      try {
        localStorage.setItem('sidebar-project-menu-open', String(next));
      } catch {
        // Ignore storage failures so navigation still works.
      }
      return next;
    });
  };

  const initials =
    user?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'TS';

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img className="brand-icon" src="/tatsavi-lotus.png" alt="Tatsavi Spaces" />
        <div>
          <span className="brand-name">TATSAVI</span>
          <span className="brand-sub">Spaces</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} className="nav-section">
            <div className="nav-section-label">{section.label}</div>
            {section.items
              .filter((item) => !item.adminOnly || user?.role === 'admin')
              .map(({ to, icon, label, children }) => {
                const sectionOpen = location.pathname.startsWith('/projects') && label === 'Projects';
                const submenuOpen = Boolean(children?.length && sectionOpen && projectMenuOpen);
                return (
                  <div key={to} className={`nav-group${submenuOpen ? ' open' : ''}`}>
                    <div className="nav-item-row">
                      <NavLink
                        to={to}
                        className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                        onClick={onNavigate}
                      >
                        <span className="nav-icon">{icon}</span>
                        <span className="nav-label">{label}</span>
                      </NavLink>
                      {children?.length && sectionOpen ? (
                        <button
                          type="button"
                          className={`nav-toggle${projectMenuOpen ? ' open' : ''}`}
                          aria-label={projectMenuOpen ? 'Hide project stages' : 'Show project stages'}
                          aria-expanded={projectMenuOpen}
                          onClick={toggleProjectMenu}
                        >
                          <span>▾</span>
                        </button>
                      ) : null}
                    </div>
                    {submenuOpen ? (
                      <div className="nav-submenu">
                        {children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            className={() => `nav-subitem${selectedStage === child.stage ? ' active' : ''}`}
                            onClick={onNavigate}
                          >
                            <span className="nav-subdot" />
                            <span className="nav-label">{child.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="user-avatar">{initials}</div>
          <div className="user-info">
            <span className="user-name">{user?.name}</span>
            <span className={`badge badge-${user?.role}`}>
              {user?.role}
            </span>
          </div>
        </div>

        <button
          className="logout-btn"
          onClick={logout}
          title="Sign out"
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}
